/* @flow */

import * as React from 'react';
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';

import {Button} from '../button';
import {DownloadIcon} from '../icon';
import {SelectMenu, type SelectMenuOption} from '../select-menu';

import './action-menu.module.css';

export type {SelectMenuOption};

type ActionMenuProps = {|
    onClick?: () => any,
    onOpenChange?: (isOpen: boolean) => void,
    options: SelectMenuOption[],
    getRef?: (?HTMLElement) => void,
    size?: 'normal' | 'small' | 'tiny',
    hasHoverBackground?: boolean,
    hasDarkHoverBackground?: boolean,
    as?: 'div' | 'button',
    showAsButton?: boolean,
    buttonText?: string,
    callMountId?: string,
    padding?: 'none' | 'less' | 'normal' | 'horizontal',
    dotColor?: 'grey' | 'off-white',
    offsetX?: number,
    offsetY?: number,
    fitContent?: boolean,
|};

type ActionMenuState = {
    isOpen: boolean,
    tooltipText: ?string,
};

export class ActionMenu extends React.PureComponent<ActionMenuProps, ActionMenuState> {
    anchor: ?HTMLElement;
    static defaultProps = {
        size: 'normal',
        as: 'button',
        dotColor: 'grey',
        padding: 'normal',
        offsetX: 0,
        offsetY: 0,
    };

    constructor() {
        super();
        this.state = {
            isOpen: false,
            tooltipText: null,
        };
    }

    componentDidUpdate(prevProps: ActionMenuProps, prevState: ActionMenuState) {
        if (this.state.tooltipText && this.state.tooltipText !== prevState.tooltipText) {
            ReactTooltip.show(this.anchor);
            setTimeout(() => {
                ReactTooltip.hide(this.anchor);
                this.setState({tooltipText: null});
            }, 2000);
        }

        if (this.state.isOpen !== prevState.isOpen) {
            if (this.props.onOpenChange) {
                this.props.onOpenChange(this.state.isOpen);
            }
        }
    }

    render() {
        const dotsContainerStyle = classnames('dots-container', {
            'dots-container--hover': this.props.hasHoverBackground,
            'dots-container--dark-hover': this.props.hasDarkHoverBackground,
            'dots-container--tiny': this.props.size === 'tiny',
            'dots-container--less-padding': this.props.padding === 'less',
            'dots-container--no-padding': this.props.padding === 'none',
            'dots-container--horizontal-padding': this.props.padding === 'horizontal',
            'dots-container--fit-content': this.props.fitContent,
        });

        const dotStyle = classnames('dot', {
            'dot--small': this.props.size === 'small',
            'dot--tiny': this.props.size === 'tiny',
            'dot--grey': this.props.dotColor === 'grey',
            'dot--off-white': this.props.dotColor === 'off-white',
        });

        const Tag = this.props.as;

        return (
            <>
                {this.props.showAsButton ? (
                    <div id={this.props.callMountId}>
                        <Button
                            size='big'
                            icon={DownloadIcon}
                            variant='primary'
                            onClick={this.handleButtonClick}
                            getButtonRef={(node) => {
                                this.anchor = node;
                                if (this.props.getRef) {
                                    this.props.getRef(node);
                                }
                            }}
                        >
                            {this.props.buttonText || 'Export'}
                        </Button>
                    </div>
                ) : (
                    /* $FlowIgnore this works fine */
                    <Tag
                        id={this.props.callMountId}
                        onClick={this.handleButtonClick}
                        styleName={dotsContainerStyle}
                        ref={(node) => {
                            this.anchor = node;
                            if (this.props.getRef) {
                                this.props.getRef(node);
                            }
                        }}
                        data-tip={this.state.tooltipText}
                        data-delay-show='0'
                        data-place='bottom'
                    >
                        <div styleName={dotStyle} />
                        <div styleName={dotStyle} />
                        <div styleName={dotStyle} />
                    </Tag>
                )}
                {this.state.isOpen ? (
                    <SelectMenu
                        options={this.props.options}
                        anchor={this.anchor}
                        location='bottom-left'
                        offsetX={this.props.offsetX}
                        offsetY={this.props.offsetY}
                        customStyles={
                            this.props.showAsButton ? {marginRight: -16} : {marginLeft: -16}
                        }
                        onClose={this.handleMenuClose}
                    />
                ) : undefined}
            </>
        );
    }

    handleButtonClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.props.onClick) {
            this.props.onClick();
        }

        this.setState(toggleOpen);
    };

    handleMenuClose = (e?: SyntheticEvent<*>, tooltipText?: ?string = null) => {
        if (e && !e.isPropagationStopped()) {
            e.stopPropagation();
        }

        this.setState({
            isOpen: false,
            tooltipText: tooltipText,
        });
    };
}

function toggleOpen(prevState) {
    return {
        isOpen: !prevState.isOpen,
    };
}
